import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { NUDGES } from '@erectus/shared';
import type { SessionLengthMin, SessionSummary } from '@erectus/shared';
import { localDayKey } from '@/lib/repo.js';
import { useWarm } from '@/lib/warmCache.js';
import { loaders, warmKeys } from '@/lib/warmup.js';
import { Button, Card } from '@/ui/primitives.js';
import { Icon } from '@/ui/icons.js';
import { useOrigin } from '@/ui/Layout.js';
import { useAuth } from '@/stores/auth.js';
import { useSession } from '@/stores/session.js';
import { SnackCard } from './SnackCard.js';
import { useHabits, type ProgramToday } from './useProgram.js';

/**
 * Today: the camera check if one is planned, rest breaks taken, and the
 * day's exercise snacks. On the Plan screen in full; on Progress (id
 * "today", where break nudges land) without the snack details.
 */
export function TodayPanel({ today, compact = false }: { today: ProgramToday | null; compact?: boolean }): ReactNode {
  const { user, userDoc } = useAuth();
  const origin = useOrigin();
  const habits = useHabits();
  const [sessionsState] = useWarm<Array<SessionSummary & { id: string }>>(
    user ? warmKeys.sessions(user.uid) : null,
    () => loaders.sessions(user!.uid),
    'Could not load your history.',
  );
  const todayKey = localDayKey(Date.now());
  const checkedToday =
    sessionsState.status === 'ready' ? sessionsState.data.find((s) => localDayKey(s.startedAt) === todayKey) ?? null : null;
  const navigate = useNavigate();
  const setPlanned = useSession((s) => s.setPlanned);
  const breakEvery = userDoc?.settings?.breakEveryMin || today?.week.breaks.everyMin || NUDGES.breakEveryMinDefault;
  const breakMinutes = today?.week.breaks.minutes ?? 3;
  const doneSnackIds = new Set(habits.snacksToday.map((s) => s.snackId));
  const lastBreak = habits.breaksToday[0]?.at ?? null;

  return (
    <Card id="today" className="scroll-mt-4 space-y-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-semibold">Today</h2>
        {today ? (
          <span className="text-xs text-muted">
            {today.program.name} · week {today.week.week}, day {today.day.day}
          </span>
        ) : null}
      </div>

      {today?.day.check ? (
        <div className="flex items-center gap-3 rounded-xl bg-surface p-3">
          <Icon name="session" size={22} className="shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-semibold">Camera check today · {today.checkMin} min</p>
            <p className="text-xs text-muted">
              {today.week.week === 1 && today.day.day === 1
                ? 'Your starting point. Every later week is compared with it.'
                : 'Same desk, same time of day, so the weeks compare fairly.'}
            </p>
          </div>
          {checkedToday ? (
            <Link
              to={`/report/${checkedToday.id}`}
              state={origin}
              className="flex shrink-0 items-center gap-1 text-sm font-semibold text-[color:var(--color-state-good)]"
            >
              <Icon name="check" size={16} strokeWidth={2.4} /> Done
            </Link>
          ) : (
            <Button
              className="shrink-0 px-4"
              onClick={() => {
                setPlanned(today.checkMin as SessionLengthMin);
                navigate('/capture');
              }}
            >
              Start
            </Button>
          )}
        </div>
      ) : null}

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold">Rest breaks</p>
          <span className="text-sm tabular-nums text-ink-soft">{habits.breaksToday.length} today</span>
        </div>
        <p className="text-xs text-muted">
          Get up every {breakEvery} minutes or so for about {breakMinutes} min: stand, walk, look away from the screen.
          {lastBreak
            ? ` Last one at ${new Date(lastBreak).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}.`
            : ''}
        </p>
        <Button variant="secondary" onClick={() => void habits.addBreak(window.location.hash === '#today' ? 'nudge' : 'manual')}>
          I took a break
        </Button>
      </div>

      {today ? (
        today.day.rest ? (
          <p className="rounded-xl bg-surface p-3 text-sm text-ink-soft">
            Rest day from exercise snacks. Keep taking your breaks.
          </p>
        ) : compact ? (
          <div className="space-y-1">
            <p className="text-sm font-semibold">
              Exercise snacks · {today.snacks.filter((s) => doneSnackIds.has(s.id)).length} of {today.snacks.length}
            </p>
            <ul className="space-y-1 text-sm text-ink-soft">
              {today.snacks.map((s) => (
                <li key={s.id} className="flex items-center gap-2">
                  {doneSnackIds.has(s.id) ? (
                    <Icon name="check" size={16} strokeWidth={2.4} className="text-[color:var(--color-state-good)]" />
                  ) : (
                    <span className="inline-block size-4 rounded-full border border-line" aria-hidden />
                  )}
                  {s.title} · {s.minutes} min
                </li>
              ))}
            </ul>
            <Link to="/program" className="inline-block pt-1 text-sm font-semibold underline">
              Open today’s plan
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm font-semibold">Exercise snacks</p>
            {today.snacks.map((snack) => (
              <SnackCard
                key={snack.id}
                snack={snack}
                done={doneSnackIds.has(snack.id)}
                onDone={() => void habits.finishSnack(snack, today.program.id)}
              />
            ))}
          </div>
        )
      ) : compact ? (
        <Link to="/program" className="block text-sm font-semibold underline">
          Pick a 4- or 6-week program
        </Link>
      ) : null}
    </Card>
  );
}
